/**
 * Modern Contact Hero - Torch Type (Refactored)
 * Matches the "Beam" design with editable content
 */

"use client";

import React from "react";
import {
    PiEnvelope,
    PiPhone,
    PiChatCircle,
    PiBinoculars,
    PiUsers,
    PiArrowRight
} from "react-icons/pi";
import EditableText from "../../core/EditableText";

// ==========================================
// Types
// ==========================================

export interface ContactItem {
    id: string;
    icon?: string;
    label: string;
    value?: string;
    type: 'email' | 'phone' | 'chat' | 'custom';
}

export interface TorchContactHeroProps {
    content: any;
    onContentChange: (newContent: any) => void;

    // Styling
    primaryColor?: string;
    fontFamily?: string;
}

// ==========================================
// Main Component
// ==========================================

export default function TorchContactHero({
    content = {},
    onContentChange,
    primaryColor = "#0F766E",
    fontFamily
}: TorchContactHeroProps) {

    // Helper to update content safely
    const updateContent = (field: string, value: any) => {
        onContentChange?.({
            ...content,
            [field]: value
        });
    };

    return (
        <section
            className="relative overflow-hidden w-full min-h-[500px] flex items-center"
            style={{
                backgroundColor: primaryColor,
                fontFamily
            }}
        >
            {/* Background Texture (Subtle) */}
            <div className="absolute inset-0 opacity-10 pointer-events-none"
                style={{ backgroundImage: `radial-gradient(circle at 30% 50%, white 0%, transparent 40%)` }}
            />

            <div className="w-full max-w-7xl mx-auto px-6 relative h-full flex flex-col lg:flex-row items-center">

                {/* LEFT SIDE: "PERSON/SOURCE" AREA */}
                {/* In the design, this is the 'source' of the beam. We use a placeholder Illustration concept. */}
                <div className="lg:w-[35%] w-full h-[60px] lg:h-full flex flex-col justify-center items-center lg:items-end relative z-10 lg:pr-10 mb-8 lg:mb-0 order-1">
                    {/* The "Person" visual - represented by an icon/avatar group concept */}
                    <div className="relative group">
                        <div className="w-40 h-40 lg:w-56 lg:h-56 rounded-full bg-white/10 backdrop-blur-sm border-2 border-white/20 flex items-center justify-center text-white shadow-2xl">
                            <PiUsers className="w-20 h-20 lg:w-28 lg:h-28 opacity-90 drop-shadow-lg" />
                        </div>

                        {/* Interactive Hint */}
                        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs text-white border border-white/10 whitespace-nowrap">
                            Our Team
                        </div>
                    </div>
                </div>

                {/* RIGHT SIDE: THE BEAM & CONTENT */}
                {/* We use a Clip Path to create the "Beam" coming from the left */}
                <div className="lg:w-[65%] w-full relative z-20 order-2">

                    {/* The White Beam Background */}
                    {/* On Desktop: Expands from Left. On Mobile: Is a card. */}
                    <div
                        className="relative bg-white lg:min-h-[400px] p-8 lg:p-16 flex flex-col justify-center rounded-2xl lg:rounded-none lg:rounded-r-3xl shadow-2xl lg:shadow-none"
                        style={{
                            clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 5% 100%, 0% 50%)", // Fallback shape
                        }}
                    >
                        {/* CSS Override for better beam shape on large screens */}
                        <style jsx>{`
                            @media (min-width: 1024px) {
                                .beam-container {
                                    clip-path: polygon(10% 20%, 100% 0%, 100% 100%, 0% 80%);
                                    margin-left: -50px;
                                    padding-left: 120px;
                                }
                            }
                            @media (max-width: 1023px) {
                                .beam-container {
                                    clip-path: none;
                                    background: white;
                                    border-radius: 20px;
                                }
                            }
                        `}</style>

                        <div className="beam-container bg-white h-full w-full flex flex-col justify-center">

                            {/* Support Pill */}
                            <div className="inline-flex items-center gap-2 self-start mb-6 px-3 py-1 bg-green-50 text-green-700 text-xs font-bold uppercase tracking-wider rounded-full border border-green-100">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                <EditableText
                                    value={content.supportText || "24/7 Support Available"}
                                    onChange={(val) => updateContent('supportText', val)}
                                />
                            </div>

                            {/* Title */}
                            <h2 className="text-4xl lg:text-6xl font-extrabold text-gray-900 mb-4 leading-tight">
                                <EditableText
                                    value={content.title || "Contact Our Team"}
                                    onChange={(val) => updateContent('title', val)}
                                />
                            </h2>

                            {/* Subtitle */}
                            <div className="text-lg text-gray-600 mb-10 max-w-xl leading-relaxed">
                                <EditableText
                                    value={content.subtitle || "We are ready to lead you into the future of education."}
                                    onChange={(val) => updateContent('subtitle', val)}
                                />
                            </div>

                            {/* Contact Actions Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                {/* EMAIL ACTION */}
                                <div className="group flex items-center p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-green-50 hover:border-green-200 transition-all duration-300">
                                    <div className="w-10 h-10 rounded-full bg-white text-green-600 shadow-sm flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                                        <PiEnvelope size={20} />
                                    </div>
                                    <div className="overflow-hidden">
                                        <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Email Us</div>
                                        <div className="font-medium text-gray-900 truncate">
                                            <EditableText
                                                value={content.email || "hello@example.com"}
                                                onChange={(val) => updateContent('email', val)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* PHONE ACTION */}
                                <div className="group flex items-center p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-green-50 hover:border-green-200 transition-all duration-300">
                                    <div className="w-10 h-10 rounded-full bg-white text-green-600 shadow-sm flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                                        <PiPhone size={20} />
                                    </div>
                                    <div className="overflow-hidden">
                                        <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Call Us</div>
                                        <div className="font-medium text-gray-900 truncate">
                                            <EditableText
                                                value={content.phone || "+1 (555) 123-4567"}
                                                onChange={(val) => updateContent('phone', val)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* CHAT ACTION */}
                                <div className="group flex items-center p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-green-50 hover:border-green-200 transition-all duration-300 cursor-pointer">
                                    <div className="w-10 h-10 rounded-full bg-white text-green-600 shadow-sm flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                                        <PiChatCircle size={20} />
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Live Chat</div>
                                        <div className="font-medium text-gray-900">Start Chat</div>
                                    </div>
                                </div>

                                {/* LOCATION ACTION */}
                                <div className="group flex items-center p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-green-50 hover:border-green-200 transition-all duration-300">
                                    <div className="w-10 h-10 rounded-full bg-white text-green-600 shadow-sm flex items-center justify-center mr-4 group-hover:scale-110 transition-transform">
                                        <PiBinoculars size={20} />
                                    </div>
                                    <div className="overflow-hidden">
                                        <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Visit</div>
                                        <div className="font-medium text-gray-900 truncate">
                                            <EditableText
                                                value={content.locationText || "San Francisco, CA"}
                                                onChange={(val) => updateContent('locationText', val)}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
